import { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ticketsAPI, contactsAPI, companiesAPI, productsAPI, resourceDocumentsAPI } from '../api';
import api from '../api';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '../components/ui/table';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { 
  Plus, Search, TicketIcon, Clock, AlertCircle, Edit, Trash2, CheckCircle, 
  ArrowUpRight, User, Wrench, Headphones, FileText, Upload, X, Eye,
  TrendingUp, TrendingDown, BarChart3, AlertTriangle, MessageSquare, Flag,
  Settings, Timer, Target, Activity, Send, Mail, ListTodo, Bot, Phone
} from 'lucide-react';
import { format, formatDistanceToNow, isPast } from 'date-fns';
import { ThreadedComments } from '../components/ThreadedComments';
import { EmailCompose } from '../components/EmailCompose';
import { AssigneeSelect } from '../components/AssigneeSelect';

// Ticket categories
const TICKET_CATEGORIES = [
  { value: 'service', label: 'Service', icon: Headphones, color: 'bg-blue-100 text-blue-700', description: 'Customer-driven issues' },
  { value: 'technical', label: 'Technical', icon: Wrench, color: 'bg-red-100 text-red-700', description: 'Machine/technology problems' }
];

// Ticket priorities
const TICKET_PRIORITIES = [
  { value: 'low', label: 'Low', color: 'bg-slate-100 text-slate-700' },
  { value: 'medium', label: 'Medium', color: 'bg-blue-100 text-blue-700' },
  { value: 'high', label: 'High', color: 'bg-orange-100 text-orange-700' },
  { value: 'urgent', label: 'Urgent', color: 'bg-red-100 text-red-700' }
];

// Ticket statuses
const TICKET_STATUSES = [
  { value: 'open', label: 'Open', color: 'bg-blue-100 text-blue-700' },
  { value: 'in_progress', label: 'In Progress', color: 'bg-amber-100 text-amber-700' },
  { value: 'waiting_on_customer', label: 'Waiting on Customer', color: 'bg-purple-100 text-purple-700' },
  { value: 'escalated', label: 'Escalated', color: 'bg-red-100 text-red-700' },
  { value: 'resolved', label: 'Resolved', color: 'bg-emerald-100 text-emerald-700' },
  { value: 'closed', label: 'Closed', color: 'bg-slate-100 text-slate-700' }
];

export const TicketsPage = () => {
  const { user, hasRole } = useAuth();
  const [tickets, setTickets] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [products, setProducts] = useState([]);
  const [dashboardMetrics, setDashboardMetrics] = useState(null);
  const [urgentTickets, setUrgentTickets] = useState([]);
  const [slaAlerts, setSlaAlerts] = useState({ summary: { total_alerts: 0, critical: 0, warning: 0, breached: 0 }, alerts: [] });
  const [slaConfig, setSlaConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('list');
  
  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');
  
  // Dialogs
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [isEscalateOpen, setIsEscalateOpen] = useState(false);
  const [isSLAConfigOpen, setIsSLAConfigOpen] = useState(false);
  const [isEmailComposeOpen, setIsEmailComposeOpen] = useState(false);
  const [isFollowUpOpen, setIsFollowUpOpen] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [creatingTask, setCreatingTask] = useState(false);
  const fileInputRef = useRef(null);
  
  // Follow-up task form
  const [followUpForm, setFollowUpForm] = useState({
    title: '',
    description: '',
    assigned_to: '',
    priority: 'medium',
    due_date: ''
  });
  
  // Form data
  const [formData, setFormData] = useState({
    subject: '',
    description: '',
    category: 'service',
    type: 'support',
    priority: 'medium',
    customer_id: '',
    company_id: '',
    product_id: '',
    assigned_to: '',
    tags: []
  });
  
  const [newComment, setNewComment] = useState('');
  const [isInternalComment, setIsInternalComment] = useState(false);
  const [escalationReason, setEscalationReason] = useState('');
  const [newTag, setNewTag] = useState('');
  const [companies, setCompanies] = useState([]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [ticketsRes, contactsRes, productsRes, companiesRes, metricsRes, urgentRes, alertsRes] = await Promise.allSettled([
        ticketsAPI.getAll({ limit: 50 }),
        contactsAPI.getAll({ limit: 100 }),
        productsAPI.getAll(),
        companiesAPI.getAll(),
        ticketsAPI.getDashboardMetrics(),
        ticketsAPI.getUrgentTickets(),
        ticketsAPI.getSLAAlerts()
      ]);
      
      if (ticketsRes.status === 'fulfilled') setTickets(ticketsRes.value.data || []);
      if (contactsRes.status === 'fulfilled') setContacts(contactsRes.value.data || []);
      if (productsRes.status === 'fulfilled') setProducts(productsRes.value.data || []);
      if (companiesRes.status === 'fulfilled') setCompanies(companiesRes.value.data || []);
      if (metricsRes.status === 'fulfilled') setDashboardMetrics(metricsRes.value.data);
      if (urgentRes.status === 'fulfilled') setUrgentTickets(urgentRes.value.data || []);
      if (alertsRes.status === 'fulfilled') {
        const alertData = alertsRes.value.data || {};
        setSlaAlerts({
          summary: alertData.summary || { total_alerts: 0, critical: 0, warning: 0, breached: 0 },
          alerts: alertData.alerts || []
        });
      }
      
      // Try to get SLA config
      try {
        const slaRes = await ticketsAPI.getSLAConfig();
        setSlaConfig(slaRes.data);
      } catch (e) {
        // Use defaults
        setSlaConfig({
          response_time_urgent: 4, response_time_high: 8,
          response_time_medium: 12, response_time_low: 24,
          resolution_time_urgent: 12, resolution_time_high: 24,
          resolution_time_medium: 48, resolution_time_low: 72
        });
      }
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await ticketsAPI.create(formData);
      toast.success('Ticket created');
      setIsDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create ticket');
    }
  };

  const handleStatusChange = async (ticketId, newStatus) => {
    try {
      await ticketsAPI.update(ticketId, { status: newStatus });
      toast.success('Status updated');
      fetchData();
      if (selectedTicket?.id === ticketId) {
        const updated = await ticketsAPI.getById(ticketId);
        setSelectedTicket(updated.data);
      }
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleAddComment = async () => {
    if (!newComment.trim() || !selectedTicket) return;
    try {
      await ticketsAPI.addComment(selectedTicket.id, {
        content: newComment,
        is_internal: isInternalComment
      });
      toast.success('Comment added');
      setNewComment('');
      setIsInternalComment(false);
      const updated = await ticketsAPI.getById(selectedTicket.id);
      setSelectedTicket(updated.data);
      fetchData();
    } catch (error) {
      toast.error('Failed to add comment');
    }
  };

  const handleEscalate = async () => {
    if (!escalationReason.trim() || !selectedTicket) return;
    try {
      await ticketsAPI.escalate(selectedTicket.id, { reason: escalationReason });
      toast.success('Ticket escalated to Technical Support');
      setIsEscalateOpen(false);
      setEscalationReason('');
      const updated = await ticketsAPI.getById(selectedTicket.id);
      setSelectedTicket(updated.data);
      fetchData();
    } catch (error) {
      toast.error('Failed to escalate ticket');
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !selectedTicket) return;
    
    setUploading(true);
    try {
      await ticketsAPI.addAttachment(selectedTicket.id, file);
      toast.success('Attachment added');
      const updated = await ticketsAPI.getById(selectedTicket.id);
      setSelectedTicket(updated.data);
    } catch (error) {
      toast.error('Failed to upload attachment');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this ticket?')) return;
    try {
      await ticketsAPI.delete(id);
      toast.success('Ticket deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete ticket');
    }
  };

  const handleSaveSLAConfig = async () => {
    try {
      await ticketsAPI.updateSLAConfig(slaConfig);
      toast.success('SLA configuration saved');
      setIsSLAConfigOpen(false);
    } catch (error) {
      toast.error('Failed to save SLA configuration');
    }
  };

  const handleCreateFollowUp = async () => {
    if (!followUpForm.title || !selectedTicket) {
      toast.error('Please provide a task title');
      return;
    }
    
    setCreatingTask(true);
    try {
      const taskData = {
        title: followUpForm.title,
        description: followUpForm.description || `Follow-up for ticket: ${selectedTicket.subject}`,
        assigned_to: followUpForm.assigned_to || '',
        priority: followUpForm.priority,
        due_date: followUpForm.due_date || null,
        status: 'todo',
        source_type: 'ticket',
        source_id: selectedTicket.id,
        source_reference: `TKT-${selectedTicket.id.slice(0, 8).toUpperCase()}`
      };
      
      await api.post('/tasks', taskData);
      toast.success('Follow-up task created');
      setIsFollowUpOpen(false);
      setFollowUpForm({
        title: '',
        description: '',
        assigned_to: '',
        priority: 'medium',
        due_date: ''
      });
    } catch (error) {
      toast.error('Failed to create follow-up task');
    } finally {
      setCreatingTask(false);
    }
  };

  const openFollowUpDialog = () => {
    setFollowUpForm({
      title: `Follow-up: ${selectedTicket?.subject}`,
      description: `Follow-up task for ticket: ${selectedTicket?.subject}\n\nCustomer: ${contacts.find(c => c.id === selectedTicket?.customer_id)?.email || 'Unknown'}\nPriority: ${selectedTicket?.priority}`,
      assigned_to: '',
      priority: selectedTicket?.priority || 'medium',
      due_date: ''
    });
    setIsFollowUpOpen(true);
  };

  const openTicketDetail = async (ticket) => {
    try {
      const res = await ticketsAPI.getById(ticket.id);
      setSelectedTicket(res.data);
      setIsDetailOpen(true);
    } catch (error) {
      toast.error('Failed to load ticket details');
    }
  };

  const resetForm = () => {
    setFormData({
      subject: '',
      description: '',
      category: 'service',
      type: 'support',
      priority: 'medium',
      customer_id: '',
      product_id: '',
      tags: []
    });
  };

  const addTag = () => {
    if (!newTag.trim()) return;
    setFormData(prev => ({
      ...prev,
      tags: [...prev.tags, newTag.trim()]
    }));
    setNewTag('');
  };

  const removeTag = (tag) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(t => t !== tag)
    }));
  };

  const getCustomerName = (customerId) => {
    const contact = contacts.find(c => c.id === customerId);
    return contact ? `${contact.first_name} ${contact.last_name}` : 'Unknown';
  };

  const getPriorityInfo = (priority) => TICKET_PRIORITIES.find(p => p.value === priority) || TICKET_PRIORITIES[1];
  const getStatusInfo = (status) => TICKET_STATUSES.find(s => s.value === status) || TICKET_STATUSES[0];
  const getCategoryInfo = (category) => TICKET_CATEGORIES.find(c => c.value === category) || TICKET_CATEGORIES[0];

  const filteredTickets = tickets.filter(ticket => {
    const matchesSearch = ticket.subject?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          ticket.description?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || ticket.status === statusFilter;
    const matchesCategory = categoryFilter === 'all' || ticket.category === categoryFilter;
    const matchesPriority = priorityFilter === 'all' || ticket.priority === priorityFilter;
    return matchesSearch && matchesStatus && matchesCategory && matchesPriority;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="tickets-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold font-['Manrope'] heading-tight">Support Tickets</h1>
          <p className="text-muted-foreground mt-1">Manage customer support and technical issues</p>
        </div>
        <div className="flex items-center gap-2">
          {hasRole(['admin']) && (
            <Button variant="outline" onClick={() => setIsSLAConfigOpen(true)} data-testid="sla-config-btn">
              <Settings className="h-4 w-4 mr-2" />
              SLA Settings
            </Button>
          )}
          <Dialog open={isDialogOpen} onOpenChange={(open) => {
            setIsDialogOpen(open);
            if (!open) resetForm();
          }}>
            <DialogTrigger asChild>
              <Button data-testid="create-ticket-btn">
                <Plus className="h-4 w-4 mr-2" />
                New Ticket
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle>Create New Ticket</DialogTitle>
                <DialogDescription>Create a support or technical ticket</DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label>Subject *</Label>
                  <Input
                    value={formData.subject}
                    onChange={(e) => handleChange('subject', e.target.value)}
                    required
                    data-testid="ticket-subject-input"
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Category *</Label>
                    <Select value={formData.category} onValueChange={(v) => handleChange('category', v)}>
                      <SelectTrigger data-testid="ticket-category-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {TICKET_CATEGORIES.map(cat => (
                          <SelectItem key={cat.value} value={cat.value}>
                            <span className="flex items-center gap-2">
                              <cat.icon className="h-4 w-4" />
                              {cat.label}
                            </span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground">
                      {getCategoryInfo(formData.category).description}
                    </p>
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Priority *</Label>
                    <Select value={formData.priority} onValueChange={(v) => handleChange('priority', v)}>
                      <SelectTrigger data-testid="ticket-priority-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {TICKET_PRIORITIES.map(p => (
                          <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Customer</Label>
                    <Select value={formData.customer_id} onValueChange={(v) => handleChange('customer_id', v)}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select customer" />
                      </SelectTrigger>
                      <SelectContent>
                        {contacts.slice(0, 50).map(c => (
                          <SelectItem key={c.id} value={c.id}>
                            {c.first_name} {c.last_name} - {c.email}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Company</Label>
                    <Select value={formData.company_id || "none"} onValueChange={(v) => handleChange('company_id', v === "none" ? "" : v)}>
                      <SelectTrigger data-testid="ticket-company-select">
                        <SelectValue placeholder="Select company (optional)" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">-- No Company --</SelectItem>
                        {companies.map(c => (
                          <SelectItem key={c.id} value={c.id}>
                            {c.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label>Assign To</Label>
                  <AssigneeSelect
                    value={formData.assigned_to}
                    onValueChange={(v) => handleChange('assigned_to', v === 'unassigned' ? '' : v)}
                    includeAI={true}
                    filterByQueue="support"
                    placeholder="Select assignee"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>Description *</Label>
                  <Textarea
                    value={formData.description}
                    onChange={(e) => handleChange('description', e.target.value)}
                    rows={4}
                    required
                    data-testid="ticket-description-input"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>Tags (for common problems tracking)</Label>
                  <div className="flex gap-2">
                    <Input
                      value={newTag}
                      onChange={(e) => setNewTag(e.target.value)}
                      placeholder="Add tag..."
                      onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
                    />
                    <Button type="button" variant="outline" onClick={addTag}>Add</Button>
                  </div>
                  {formData.tags.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-2">
                      {formData.tags.map(tag => (
                        <Badge key={tag} variant="outline" className="flex items-center gap-1">
                          {tag}
                          <X className="h-3 w-3 cursor-pointer" onClick={() => removeTag(tag)} />
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>
                
                <DialogFooter>
                  <Button type="submit" data-testid="ticket-submit-btn">Create Ticket</Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* SLA Alert Banner - shown when there are alerts */}
      {slaAlerts?.summary?.total_alerts > 0 && (
        <Card className={`border-l-4 ${slaAlerts?.summary?.critical > 0 ? 'border-l-red-500 bg-red-50' : 'border-l-amber-500 bg-amber-50'}`}>
          <CardContent className="py-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-full ${slaAlerts?.summary?.critical > 0 ? 'bg-red-100' : 'bg-amber-100'}`}>
                  <AlertCircle className={`h-5 w-5 ${slaAlerts?.summary?.critical > 0 ? 'text-red-600' : 'text-amber-600'}`} />
                </div>
                <div>
                  <p className="font-semibold text-sm">
                    {slaAlerts?.summary?.breached > 0 
                      ? `${slaAlerts?.summary?.breached} SLA ${slaAlerts?.summary?.breached === 1 ? 'Breach' : 'Breaches'} Detected!`
                      : `${slaAlerts?.summary?.total_alerts} ${slaAlerts?.summary?.total_alerts === 1 ? 'Ticket' : 'Tickets'} At Risk`
                    }
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {slaAlerts?.summary?.critical > 0 && <span className="text-red-600 font-medium">{slaAlerts?.summary?.critical} critical</span>}
                    {slaAlerts?.summary?.critical > 0 && slaAlerts?.summary?.warning > 0 && ' • '}
                    {slaAlerts?.summary?.warning > 0 && <span className="text-amber-600">{slaAlerts?.summary?.warning} warning</span>}
                  </p>
                </div>
              </div>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => setActiveTab('sla-alerts')}
                className={slaAlerts?.summary?.critical > 0 ? 'border-red-300 hover:bg-red-100' : 'border-amber-300 hover:bg-amber-100'}
                data-testid="view-sla-alerts-btn"
              >
                View Alerts
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="list" data-testid="tab-tickets-list">
            <TicketIcon className="h-4 w-4 mr-2" />
            All Tickets
          </TabsTrigger>
          <TabsTrigger value="dashboard" data-testid="tab-tickets-dashboard">
            <BarChart3 className="h-4 w-4 mr-2" />
            Dashboard
          </TabsTrigger>
          <TabsTrigger value="urgent" data-testid="tab-tickets-urgent">
            <AlertTriangle className="h-4 w-4 mr-2" />
            Urgent ({urgentTickets.length})
          </TabsTrigger>
          <TabsTrigger 
            value="sla-alerts" 
            data-testid="tab-sla-alerts"
            className={slaAlerts?.summary?.critical > 0 ? 'text-red-600' : ''}
          >
            <Clock className="h-4 w-4 mr-2" />
            SLA Alerts {slaAlerts?.summary?.total_alerts > 0 && (
              <Badge className={`ml-2 ${slaAlerts?.summary?.critical > 0 ? 'bg-red-500' : 'bg-amber-500'}`}>
                {slaAlerts?.summary?.total_alerts}
              </Badge>
            )}
          </TabsTrigger>
        </TabsList>

        {/* Tickets List Tab */}
        <TabsContent value="list" className="space-y-4">
          {/* Filters */}
          <Card>
            <CardContent className="pt-6">
              <div className="flex flex-wrap gap-4">
                <div className="relative flex-1 min-w-[200px]">
                  <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search tickets..."
                    className="pl-10"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    data-testid="ticket-search-input"
                  />
                </div>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-[160px]">
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Statuses</SelectItem>
                    {TICKET_STATUSES.map(s => (
                      <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                  <SelectTrigger className="w-[140px]">
                    <SelectValue placeholder="Category" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Types</SelectItem>
                    {TICKET_CATEGORIES.map(c => (
                      <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={priorityFilter} onValueChange={setPriorityFilter}>
                  <SelectTrigger className="w-[130px]">
                    <SelectValue placeholder="Priority" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Priorities</SelectItem>
                    {TICKET_PRIORITIES.map(p => (
                      <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          {/* Tickets Table */}
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Ticket</TableHead>
                    <TableHead>Category</TableHead>
                    <TableHead>Priority</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Customer</TableHead>
                    <TableHead>Created</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredTickets.length > 0 ? (
                    filteredTickets.map(ticket => {
                      const catInfo = getCategoryInfo(ticket.category);
                      const priorityInfo = getPriorityInfo(ticket.priority);
                      const statusInfo = getStatusInfo(ticket.status);
                      const CatIcon = catInfo.icon;
                      const isHighPriority = ticket.priority === 'high' || ticket.priority === 'urgent';
                      
                      return (
                        <TableRow 
                          key={ticket.id} 
                          className={`cursor-pointer transition-colors ${isHighPriority ? 'bg-red-50/50 hover:bg-red-50' : 'hover:bg-slate-50'}`}
                          data-testid={`ticket-row-${ticket.id}`}
                        >
                          <TableCell onClick={() => openTicketDetail(ticket)}>
                            <div className="flex items-start gap-3">
                              <div className={`h-9 w-9 rounded-sm flex items-center justify-center ${catInfo.color}`}>
                                <CatIcon className="h-4 w-4" />
                              </div>
                              <div>
                                <p className="font-medium">{ticket.subject}</p>
                                <p className="text-xs text-muted-foreground line-clamp-1">{ticket.description}</p>
                                {ticket.is_escalated && (
                                  <Badge className="bg-red-100 text-red-700 mt-1">
                                    <ArrowUpRight className="h-3 w-3 mr-1" />
                                    Escalated
                                  </Badge>
                                )}
                              </div>
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge className={catInfo.color}>{catInfo.label}</Badge>
                          </TableCell>
                          <TableCell>
                            <Badge className={priorityInfo.color}>{priorityInfo.label}</Badge>
                          </TableCell>
                          <TableCell>
                            <Badge className={statusInfo.color}>{statusInfo.label}</Badge>
                          </TableCell>
                          <TableCell>
                            <Link 
                              to={`/contacts/${ticket.customer_id}`} 
                              className="text-blue-600 hover:underline flex items-center gap-1"
                              data-testid={`ticket-customer-link-${ticket.id}`}
                            >
                              <User className="h-3 w-3" />
                              {getCustomerName(ticket.customer_id)}
                            </Link>
                          </TableCell>
                          <TableCell>
                            <span className="text-sm text-muted-foreground">
                              {ticket.created_at ? formatDistanceToNow(new Date(ticket.created_at), { addSuffix: true }) : '-'}
                            </span>
                          </TableCell>
                          <TableCell className="text-right">
                            <Button variant="ghost" size="sm" onClick={() => openTicketDetail(ticket)} data-testid={`ticket-view-${ticket.id}`}>
                              <Eye className="h-4 w-4" />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => handleDelete(ticket.id)} className="text-red-600" data-testid={`ticket-delete-${ticket.id}`}>
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  ) : (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                        No tickets found
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Dashboard Tab */}
        <TabsContent value="dashboard" className="space-y-6">
          {dashboardMetrics && (
            <>
              {/* Metric Cards */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card>
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-muted-foreground">Open Tickets</p>
                        <p className="text-2xl font-bold">{dashboardMetrics.open_tickets}</p>
                      </div>
                      <TicketIcon className="h-8 w-8 text-blue-500" />
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-muted-foreground">Escalated</p>
                        <p className="text-2xl font-bold">{dashboardMetrics.escalated_tickets}</p>
                      </div>
                      <ArrowUpRight className="h-8 w-8 text-red-500" />
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-muted-foreground">Flagged for Review</p>
                        <p className="text-2xl font-bold">{dashboardMetrics.flagged_for_review}</p>
                      </div>
                      <Flag className="h-8 w-8 text-amber-500" />
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-muted-foreground">Resolved</p>
                        <p className="text-2xl font-bold">{dashboardMetrics.resolved_tickets}</p>
                      </div>
                      <CheckCircle className="h-8 w-8 text-emerald-500" />
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* SLA & Response Times */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Timer className="h-5 w-5" />
                      Response Time
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="flex justify-between items-center">
                      <span className="text-muted-foreground">Average</span>
                      <span className="font-semibold">{dashboardMetrics.response_time?.average_hours?.toFixed(1)} hrs</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-muted-foreground">Minimum</span>
                      <span>{dashboardMetrics.response_time?.min_hours?.toFixed(1)} hrs</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-muted-foreground">Maximum</span>
                      <span>{dashboardMetrics.response_time?.max_hours?.toFixed(1)} hrs</span>
                    </div>
                    <div className="pt-2 border-t">
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-muted-foreground">SLA Compliance</span>
                        <span className={`font-semibold ${dashboardMetrics.response_time?.sla_compliance_pct >= 80 ? 'text-emerald-600' : 'text-red-600'}`}>
                          {dashboardMetrics.response_time?.sla_compliance_pct}%
                        </span>
                      </div>
                      <Progress value={dashboardMetrics.response_time?.sla_compliance_pct || 0} className="h-2" />
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Target className="h-5 w-5" />
                      Resolution Time
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="flex justify-between items-center">
                      <span className="text-muted-foreground">Average</span>
                      <span className="font-semibold">{dashboardMetrics.resolution_time?.average_hours?.toFixed(1)} hrs</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-muted-foreground">Minimum</span>
                      <span>{dashboardMetrics.resolution_time?.min_hours?.toFixed(1)} hrs</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-muted-foreground">Maximum</span>
                      <span>{dashboardMetrics.resolution_time?.max_hours?.toFixed(1)} hrs</span>
                    </div>
                    <div className="pt-2 border-t">
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-muted-foreground">SLA Compliance</span>
                        <span className={`font-semibold ${dashboardMetrics.resolution_time?.sla_compliance_pct >= 80 ? 'text-emerald-600' : 'text-red-600'}`}>
                          {dashboardMetrics.resolution_time?.sla_compliance_pct}%
                        </span>
                      </div>
                      <Progress value={dashboardMetrics.resolution_time?.sla_compliance_pct || 0} className="h-2" />
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* By Category & Priority */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Card>
                  <CardHeader>
                    <CardTitle>By Category</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Headphones className="h-4 w-4 text-blue-600" />
                          <span>Service Tickets</span>
                        </div>
                        <Badge className="bg-blue-100 text-blue-700">{dashboardMetrics.by_category?.service || 0}</Badge>
                      </div>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Wrench className="h-4 w-4 text-red-600" />
                          <span>Technical Tickets</span>
                        </div>
                        <Badge className="bg-red-100 text-red-700">{dashboardMetrics.by_category?.technical || 0}</Badge>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle>Common Problems</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {dashboardMetrics.common_problems?.length > 0 ? (
                      <div className="space-y-2">
                        {dashboardMetrics.common_problems.slice(0, 5).map(([tag, count], i) => (
                          <div key={tag} className="flex items-center justify-between">
                            <Badge variant="outline">{tag}</Badge>
                            <span className="text-muted-foreground">{count} tickets</span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-muted-foreground text-center py-4">No tags recorded yet</p>
                    )}
                  </CardContent>
                </Card>
              </div>
            </>
          )}
        </TabsContent>

        {/* Urgent Tickets Tab */}
        <TabsContent value="urgent" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-red-500" />
                Most Urgent Tickets
              </CardTitle>
              <CardDescription>Sorted by priority and age (oldest first)</CardDescription>
            </CardHeader>
            <CardContent>
              {urgentTickets.length > 0 ? (
                <div className="space-y-3">
                  {urgentTickets.map(ticket => {
                    const priorityInfo = getPriorityInfo(ticket.priority);
                    const statusInfo = getStatusInfo(ticket.status);
                    const catInfo = getCategoryInfo(ticket.category);
                    
                    return (
                      <div 
                        key={ticket.id} 
                        className="flex items-center justify-between p-3 bg-red-50 border border-red-100 rounded-lg hover:border-red-300 transition-colors cursor-pointer"
                        onClick={() => openTicketDetail(ticket)}
                        data-testid={`urgent-ticket-${ticket.id}`}
                      >
                        <div className="flex items-center gap-3">
                          <Badge className={priorityInfo.color}>{priorityInfo.label}</Badge>
                          <div>
                            <p className="font-medium">{ticket.subject}</p>
                            <p className="text-sm text-muted-foreground flex items-center gap-2">
                              <Link 
                                to={`/contacts/${ticket.customer_id}`} 
                                className="text-blue-600 hover:underline"
                                onClick={(e) => e.stopPropagation()}
                                data-testid={`urgent-ticket-customer-link-${ticket.id}`}
                              >
                                {ticket.customer_name || getCustomerName(ticket.customer_id)}
                              </Link>
                              <span>•</span>
                              <span>{ticket.age_hours?.toFixed(1)} hours old</span>
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge className={catInfo.color}>{catInfo.label}</Badge>
                          <Badge className={statusInfo.color}>{statusInfo.label}</Badge>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-center py-8 text-muted-foreground">No urgent tickets! 🎉</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* SLA Alerts Tab */}
        <TabsContent value="sla-alerts" className="space-y-4">
          {/* Summary Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card className="border-l-4 border-l-red-500">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">SLA Breached</p>
                    <p className="text-2xl font-bold text-red-600">{slaAlerts?.summary?.breached || 0}</p>
                  </div>
                  <AlertCircle className="h-8 w-8 text-red-500" />
                </div>
              </CardContent>
            </Card>
            <Card className="border-l-4 border-l-orange-500">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Critical Risk</p>
                    <p className="text-2xl font-bold text-orange-600">{slaAlerts?.summary?.critical || 0}</p>
                  </div>
                  <AlertTriangle className="h-8 w-8 text-orange-500" />
                </div>
              </CardContent>
            </Card>
            <Card className="border-l-4 border-l-amber-500">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Warning</p>
                    <p className="text-2xl font-bold text-amber-600">{slaAlerts?.summary?.warning || 0}</p>
                  </div>
                  <Clock className="h-8 w-8 text-amber-500" />
                </div>
              </CardContent>
            </Card>
            <Card className="border-l-4 border-l-blue-500">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Total Alerts</p>
                    <p className="text-2xl font-bold">{slaAlerts?.summary?.total_alerts || 0}</p>
                  </div>
                  <TicketIcon className="h-8 w-8 text-blue-500" />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Alerts List */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Clock className="h-5 w-5" />
                SLA Breach Alerts
              </CardTitle>
              <CardDescription>Tickets approaching or exceeding SLA deadlines</CardDescription>
            </CardHeader>
            <CardContent>
              {slaAlerts?.alerts?.length > 0 ? (
                <div className="space-y-3">
                  {slaAlerts.alerts.map(alert => {
                    const priorityInfo = getPriorityInfo(alert.priority);
                    const statusInfo = getStatusInfo(alert.status);
                    const catInfo = getCategoryInfo(alert.category);
                    const isCritical = alert.overall_severity === 'critical';
                    const isBreached = alert.alerts.some(a => a.type.endsWith('_breached'));
                    
                    return (
                      <div 
                        key={alert.ticket_id} 
                        className={`p-4 border rounded-lg cursor-pointer transition-colors ${
                          isBreached 
                            ? 'bg-red-50 border-red-200 hover:border-red-400' 
                            : isCritical 
                              ? 'bg-orange-50 border-orange-200 hover:border-orange-400'
                              : 'bg-amber-50 border-amber-200 hover:border-amber-400'
                        }`}
                        onClick={() => {
                          const ticket = tickets.find(t => t.id === alert.ticket_id);
                          if (ticket) openTicketDetail(ticket);
                        }}
                        data-testid={`sla-alert-${alert.ticket_id}`}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              {isBreached ? (
                                <Badge className="bg-red-500 text-white">SLA BREACHED</Badge>
                              ) : isCritical ? (
                                <Badge className="bg-orange-500 text-white">CRITICAL</Badge>
                              ) : (
                                <Badge className="bg-amber-500 text-white">WARNING</Badge>
                              )}
                              <Badge className={priorityInfo.color}>{priorityInfo.label}</Badge>
                              <Badge className={catInfo.color}>{catInfo.label}</Badge>
                            </div>
                            <p className="font-semibold">{alert.subject}</p>
                            <p className="text-sm text-muted-foreground mt-1">
                              <Link 
                                to={`/contacts/${alert.customer_id}`} 
                                className="text-blue-600 hover:underline"
                                onClick={(e) => e.stopPropagation()}
                              >
                                {alert.customer_name}
                              </Link>
                              <span className="mx-2">•</span>
                              <span>{statusInfo.label}</span>
                            </p>
                            
                            {/* Alert Details */}
                            <div className="mt-3 space-y-1">
                              {alert.alerts.map((alertItem, idx) => (
                                <div 
                                  key={idx} 
                                  className={`text-sm flex items-center gap-2 ${
                                    alertItem.type.endsWith('_breached') 
                                      ? 'text-red-700 font-medium' 
                                      : alertItem.severity === 'critical'
                                        ? 'text-orange-700'
                                        : 'text-amber-700'
                                  }`}
                                >
                                  {alertItem.type.endsWith('_breached') ? (
                                    <AlertCircle className="h-4 w-4" />
                                  ) : (
                                    <Clock className="h-4 w-4" />
                                  )}
                                  <span>{alertItem.message}</span>
                                  {alertItem.type.includes('response') && (
                                    <span className="text-xs text-muted-foreground">(Response SLA: {alertItem.target_hours}h)</span>
                                  )}
                                  {alertItem.type.includes('resolution') && (
                                    <span className="text-xs text-muted-foreground">(Resolution SLA: {alertItem.target_hours}h)</span>
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>
                          <Button variant="ghost" size="sm">
                            <Eye className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-8">
                  <CheckCircle className="h-12 w-12 mx-auto text-emerald-500 mb-3" />
                  <p className="text-lg font-medium">All Clear!</p>
                  <p className="text-muted-foreground">No SLA breaches or at-risk tickets</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Ticket Detail Dialog */}
      <Dialog open={isDetailOpen} onOpenChange={setIsDetailOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          {selectedTicket && (
            <>
              <DialogHeader>
                <div className="flex items-start justify-between">
                  <div>
                    <DialogTitle className="text-xl">{selectedTicket.subject}</DialogTitle>
                    <DialogDescription className="flex items-center gap-2 mt-2">
                      <Badge className={getCategoryInfo(selectedTicket.category).color}>
                        {getCategoryInfo(selectedTicket.category).label}
                      </Badge>
                      <Badge className={getPriorityInfo(selectedTicket.priority).color}>
                        {selectedTicket.priority}
                      </Badge>
                      <Badge className={getStatusInfo(selectedTicket.status).color}>
                        {selectedTicket.status?.replace('_', ' ')}
                      </Badge>
                      {selectedTicket.is_escalated && (
                        <Badge className="bg-red-100 text-red-700">Escalated</Badge>
                      )}
                    </DialogDescription>
                  </div>
                </div>
              </DialogHeader>

              <div className="grid grid-cols-3 gap-6 mt-4">
                {/* Main Content */}
                <div className="col-span-2 space-y-4">
                  <div>
                    <h4 className="font-medium mb-2">Description</h4>
                    <p className="text-muted-foreground">{selectedTicket.description}</p>
                  </div>

                  {/* Timeline */}
                  <div>
                    <h4 className="font-medium mb-2">Activity Timeline</h4>
                    <ScrollArea className="h-64 border rounded-md p-3">
                      {selectedTicket.timeline?.length > 0 ? (
                        <div className="space-y-3">
                          {selectedTicket.timeline.map((entry, i) => (
                            <div key={entry.id || i} className={`p-2 rounded ${entry.is_internal ? 'bg-amber-50 border-l-2 border-amber-400' : 'bg-slate-50'}`}>
                              <div className="flex items-center justify-between text-xs text-muted-foreground mb-1">
                                <span className="flex items-center gap-1">
                                  <User className="h-3 w-3" />
                                  {entry.user_name || 'System'}
                                </span>
                                <span>{entry.timestamp ? format(new Date(entry.timestamp), 'MMM d, h:mm a') : ''}</span>
                              </div>
                              <p className="text-sm">
                                {entry.action === 'status_change' && (
                                  <>Status changed from <Badge variant="outline" className="text-xs">{entry.old_value}</Badge> to <Badge variant="outline" className="text-xs">{entry.new_value}</Badge></>
                                )}
                                {entry.action === 'comment' && entry.content}
                                {entry.action === 'escalation' && entry.content}
                                {entry.action === 'assignment' && <>Assigned to {entry.new_value}</>}
                                {entry.action === 'attachment' && entry.content}
                                {entry.action === 'created' && 'Ticket created'}
                              </p>
                              {entry.is_internal && <Badge className="text-xs bg-amber-100 text-amber-700 mt-1">Internal Note</Badge>}
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-center py-4 text-muted-foreground">No activity yet</p>
                      )}
                    </ScrollArea>
                  </div>

                  {/* Add Comment */}
                  <div className="space-y-2">
                    <Label>Add Comment</Label>
                    <Textarea
                      value={newComment}
                      onChange={(e) => setNewComment(e.target.value)}
                      placeholder="Type your comment..."
                      rows={3}
                    />
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Checkbox
                          id="internal"
                          checked={isInternalComment}
                          onCheckedChange={setIsInternalComment}
                        />
                        <label htmlFor="internal" className="text-sm">Internal note (not visible to customer)</label>
                      </div>
                      <Button onClick={handleAddComment} disabled={!newComment.trim()}>
                        <MessageSquare className="h-4 w-4 mr-2" />
                        Add Comment
                      </Button>
                    </div>
                  </div>
                </div>

                {/* Sidebar */}
                <div className="space-y-4">
                  <div>
                    <h4 className="font-medium mb-2">Status</h4>
                    <Select value={selectedTicket.status} onValueChange={(v) => handleStatusChange(selectedTicket.id, v)}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {TICKET_STATUSES.map(s => (
                          <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <h4 className="font-medium mb-2">Customer</h4>
                    <Link to={`/contacts/${selectedTicket.customer_id}`} className="text-blue-600 hover:underline flex items-center gap-1">
                      <User className="h-4 w-4" />
                      {getCustomerName(selectedTicket.customer_id)}
                    </Link>
                    {/* Email Customer Button */}
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="w-full mt-2"
                      onClick={() => setIsEmailComposeOpen(true)}
                      data-testid="ticket-email-customer-btn"
                    >
                      <Mail className="h-4 w-4 mr-2" />
                      Email Customer
                    </Button>
                    
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="w-full mt-2"
                      onClick={openFollowUpDialog}
                      data-testid="ticket-followup-btn"
                    >
                      <ListTodo className="h-4 w-4 mr-2" />
                      Create Follow-up Task
                    </Button>
                  </div>

                  {selectedTicket.category === 'service' && !selectedTicket.is_escalated && (
                    <Button variant="outline" className="w-full" onClick={() => setIsEscalateOpen(true)}>
                      <ArrowUpRight className="h-4 w-4 mr-2" />
                      Escalate to Technical
                    </Button>
                  )}

                  {/* Attachments */}
                  <div>
                    <h4 className="font-medium mb-2">Attachments</h4>
                    {selectedTicket.attachments?.length > 0 ? (
                      <div className="space-y-2">
                        {selectedTicket.attachments.map(att => (
                          <div key={att.id} className="flex items-center gap-2 p-2 bg-slate-50 rounded">
                            <FileText className="h-4 w-4 text-blue-600" />
                            <span className="flex-1 text-sm truncate">{att.file_name}</span>
                            <Button variant="ghost" size="sm" onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}${att.file_url}`, '_blank')}>
                              <Eye className="h-4 w-4" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-muted-foreground">No attachments</p>
                    )}
                    <Button variant="outline" size="sm" className="mt-2 w-full" onClick={() => fileInputRef.current?.click()}>
                      <Upload className="h-4 w-4 mr-2" />
                      {uploading ? 'Uploading...' : 'Add Attachment'}
                    </Button>
                    <input ref={fileInputRef} type="file" className="hidden" onChange={handleFileUpload} />
                  </div>

                  {/* Tags */}
                  {selectedTicket.tags?.length > 0 && (
                    <div>
                      <h4 className="font-medium mb-2">Tags</h4>
                      <div className="flex flex-wrap gap-1">
                        {selectedTicket.tags.map(tag => (
                          <Badge key={tag} variant="outline">{tag}</Badge>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Escalate Dialog */}
      <Dialog open={isEscalateOpen} onOpenChange={setIsEscalateOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Escalate to Technical Support</DialogTitle>
            <DialogDescription>
              This will change the ticket category to Technical and notify the technical support team.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Reason for Escalation *</Label>
              <Textarea
                value={escalationReason}
                onChange={(e) => setEscalationReason(e.target.value)}
                placeholder="Explain why this needs technical support..."
                rows={4}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEscalateOpen(false)}>Cancel</Button>
            <Button onClick={handleEscalate} disabled={!escalationReason.trim()}>
              <ArrowUpRight className="h-4 w-4 mr-2" />
              Escalate Ticket
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* SLA Config Dialog */}
      <Dialog open={isSLAConfigOpen} onOpenChange={setIsSLAConfigOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>SLA Configuration</DialogTitle>
            <DialogDescription>Set response and resolution time targets (in hours)</DialogDescription>
          </DialogHeader>
          {slaConfig && (
            <div className="space-y-6">
              <div>
                <h4 className="font-medium mb-3">Response Time Targets (hours)</h4>
                <div className="grid grid-cols-2 gap-4">
                  {['urgent', 'high', 'medium', 'low'].map(priority => (
                    <div key={priority} className="space-y-1">
                      <Label className="capitalize">{priority}</Label>
                      <Input
                        type="number"
                        value={slaConfig[`response_time_${priority}`]}
                        onChange={(e) => setSlaConfig(prev => ({
                          ...prev,
                          [`response_time_${priority}`]: parseInt(e.target.value) || 0
                        }))}
                      />
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <h4 className="font-medium mb-3">Resolution Time Targets (hours)</h4>
                <div className="grid grid-cols-2 gap-4">
                  {['urgent', 'high', 'medium', 'low'].map(priority => (
                    <div key={priority} className="space-y-1">
                      <Label className="capitalize">{priority}</Label>
                      <Input
                        type="number"
                        value={slaConfig[`resolution_time_${priority}`]}
                        onChange={(e) => setSlaConfig(prev => ({
                          ...prev,
                          [`resolution_time_${priority}`]: parseInt(e.target.value) || 0
                        }))}
                      />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsSLAConfigOpen(false)}>Cancel</Button>
            <Button onClick={handleSaveSLAConfig}>Save Configuration</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Email Compose Dialog */}
      <EmailCompose
        open={isEmailComposeOpen}
        onOpenChange={setIsEmailComposeOpen}
        contact={selectedTicket?.customer_id ? {
          id: selectedTicket.customer_id,
          email: contacts.find(c => c.id === selectedTicket.customer_id)?.email,
          first_name: contacts.find(c => c.id === selectedTicket.customer_id)?.first_name,
          last_name: contacts.find(c => c.id === selectedTicket.customer_id)?.last_name
        } : null}
        ticketId={selectedTicket?.id}
        onEmailSent={() => {
          toast.success('Email sent to customer');
        }}
      />

      {/* Follow-up Task Dialog */}
      <Dialog open={isFollowUpOpen} onOpenChange={setIsFollowUpOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ListTodo className="h-5 w-5" />
              Create Follow-up Task
            </DialogTitle>
            <DialogDescription>
              Create a task linked to this ticket for follow-up
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Task Title *</Label>
              <Input
                value={followUpForm.title}
                onChange={(e) => setFollowUpForm(prev => ({ ...prev, title: e.target.value }))}
                placeholder="e.g., Follow up on customer issue"
              />
            </div>
            <div>
              <Label>Description</Label>
              <Textarea
                value={followUpForm.description}
                onChange={(e) => setFollowUpForm(prev => ({ ...prev, description: e.target.value }))}
                rows={3}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Assign To</Label>
                <AssigneeSelect
                  value={followUpForm.assigned_to}
                  onValueChange={(v) => setFollowUpForm(prev => ({ ...prev, assigned_to: v === 'unassigned' ? '' : v }))}
                  includeAI={true}
                  filterByQueue="support"
                />
              </div>
              <div>
                <Label>Priority</Label>
                <Select 
                  value={followUpForm.priority} 
                  onValueChange={(v) => setFollowUpForm(prev => ({ ...prev, priority: v }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="urgent">Urgent</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Due Date</Label>
              <Input
                type="date"
                value={followUpForm.due_date}
                onChange={(e) => setFollowUpForm(prev => ({ ...prev, due_date: e.target.value }))}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsFollowUpOpen(false)}>Cancel</Button>
            <Button onClick={handleCreateFollowUp} disabled={creatingTask || !followUpForm.title}>
              {creatingTask ? 'Creating...' : 'Create Task'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TicketsPage;
